/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Bolt,
  CalendarMonth,
  CheckCircleOutline,
  InsightsOutlined,
  TrendingUp,
  WarningAmberOutlined
} from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import NoDataOverlay from '../../components/Insights/NoDataOverlay';
import SignalInsights from '../../components/Insights/SignalInsights';
import Loading from '../../components/Loading';
import PerformanceMonthPicker from '../../components/PerformanceMonthPicker';
import usePerformanceMonthFilter from '../../hooks/usePerformanceMonthFilter';
import { sendRequest } from '../../utils/dateHelpers';
import {
  formatEndYearMonth,
  formatEvaluationInterval
} from '../../utils/performanceMonth';
import {
  getProductivityScreenOverlayContent,
  isProductivityScreenBlocked
} from '../../constants/productivityScreenState';

const SCORE_SERIES = [
  {
    key: 'performance_score',
    title: 'Performance',
    color: '#3b82f6'
  },
  {
    key: 'efficiency_score',
    title: 'Efficiency',
    color: '#f59e0b'
  },
  {
    key: 'sustainability_score',
    title: 'Sustainability',
    color: '#10b981'
  }
];

const normalizeInsightsResponse = response => {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const nestedResponse = response.data;
  const hasNestedPayload =
    nestedResponse &&
    typeof nestedResponse === 'object' &&
    ('selected_end_year_month' in nestedResponse ||
      'available_end_year_months' in nestedResponse ||
      'data' in nestedResponse);

  return hasNestedPayload ? nestedResponse : response;
};

const getScoreGradient = score => {
  if (score >= 75) return 'from-green-400 to-green-600';
  if (score >= 60) return 'from-yellow-400 to-yellow-500';
  return 'from-red-400 to-red-600';
};

const buildTrendChartOptions = (seriesData, isDarkMode) => ({
  chart: {
    type: 'spline',
    backgroundColor: 'transparent',
    height: 360,
    spacingBottom: 28
  },
  title: { text: null },
  credits: { enabled: false },
  exporting: { enabled: false },
  xAxis: {
    categories: seriesData.map(point => point.label),
    lineColor: isDarkMode ? '#334155' : '#cbd5e1',
    labels: {
      useHTML: true,
      style: {
        color: isDarkMode ? '#cbd5e1' : '#475569'
      },
      formatter() {
        const [startLabel = '', endLabel = ''] = String(this.value).split(
          ' - '
        );

        return `
          <span style="display:block; text-align:center; white-space:nowrap;">
            <span style="display:block;">${startLabel}</span>
            <span style="display:block; opacity:0.8;">${endLabel}</span>
          </span>
        `;
      }
    }
  },
  yAxis: {
    min: 0,
    max: 100,
    gridLineColor: isDarkMode ? '#1e293b' : '#e2e8f0',
    title: {
      text: 'Score',
      style: {
        color: isDarkMode ? '#cbd5e1' : '#475569'
      }
    },
    labels: {
      style: {
        color: isDarkMode ? '#cbd5e1' : '#475569'
      }
    }
  },
  tooltip: {
    shared: true,
    backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
    borderColor: isDarkMode ? '#334155' : '#cbd5e1',
    style: {
      color: isDarkMode ? '#e2e8f0' : '#0f172a'
    }
  },
  legend: {
    itemStyle: {
      color: isDarkMode ? '#e2e8f0' : '#0f172a'
    },
    itemHoverStyle: {
      color: isDarkMode ? '#ffffff' : '#020617'
    }
  },
  plotOptions: {
    series: {
      stickyTracking: true,
      marker: {
        enabled: true,
        radius: 3
      }
    }
  },
  series: SCORE_SERIES.map(item => ({
    name: item.title,
    data: seriesData.map(point => point[item.key]),
    color: item.color
  }))
});

const DashboardCard = ({ title, score, reason, advices, loading }) => {
  const gradient = getScoreGradient(score || 0);
  const isDarkMode = document?.body?.classList.contains('dark');
  const scoreValue = typeof score === 'number' ? score : 0;
  const scoreLabel =
    scoreValue >= 75 ? 'Strong' : scoreValue >= 60 ? 'On track' : 'Needs focus';

  return (
    <div
      className={`group min-h-[250px] overflow-hidden rounded-2xl border shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl ${
        isDarkMode
          ? 'border-strokedark bg-boxdark'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div
        className={`relative flex items-start bg-gradient-to-br p-5 text-white ${gradient}`}
      >
        <div className="absolute -right-7 -top-8 h-28 w-28 rounded-full bg-white/10 transition duration-500 group-hover:scale-125" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-3">
            <h5 className="text-xs font-bold uppercase tracking-[0.16em] text-white/80">
              {title}
            </h5>
            <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold text-white/90">
              {scoreLabel}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between gap-3">
            {loading ? (
              <div className="h-8 w-24 animate-pulse rounded bg-white/50" />
            ) : (
              <span className="text-4xl font-extrabold tracking-tight">
                {score ?? '—'}
              </span>
            )}
          </div>
          <div className="mt-3 h-1.5 w-full max-w-[180px] overflow-hidden rounded-full bg-white/25">
            <div
              className="h-full rounded-full bg-white transition-all duration-700"
              style={{ width: loading ? '0%' : `${scoreValue}%` }}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        <div className="flex flex-col gap-2">
          <h6 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-800 dark:text-white">
            <InsightsOutlined className="text-blue-500" fontSize="small" />
            What this means
          </h6>
          {loading ? (
            <>
              <div
                className={`h-4 w-3/4 animate-pulse rounded ${
                  isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                }`}
              />
              <div
                className={`h-4 w-full animate-pulse rounded ${
                  isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                }`}
              />
              <div
                className={`h-4 w-5/6 animate-pulse rounded ${
                  isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                }`}
              />
            </>
          ) : (
            <p className="text-sm leading-6 text-slate-600 dark:text-gray-300">
              {reason}
            </p>
          )}
        </div>

        <div
          className={`rounded-xl border-l-4 border-yellow-400 p-4 shadow-inner ${
            isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'
          }`}
        >
          <h6 className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-800 dark:text-yellow-300">
            Next best actions{' '}
            <Bolt className="text-yellow-400" fontSize="small" />
          </h6>
          <ul className="space-y-2">
            {loading
              ? Array(3)
                  .fill(0)
                  .map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-4 w-full animate-pulse rounded ${
                        isDarkMode ? 'bg-yellow-800/70' : 'bg-yellow-200'
                      }`}
                    />
                  ))
              : advices?.map((advice, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 rounded-md p-2 transition hover:bg-yellow-100 dark:hover:bg-yellow-800"
                  >
                    <CheckCircleOutline
                      className="mt-0.5 text-yellow-500"
                      fontSize="small"
                    />
                    <span className="text-sm text-gray-700 dark:text-yellow-200">
                      {advice}
                    </span>
                  </li>
                ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const V2CompanyInsightsSkeleton = ({
  isDarkMode,
  ariaLabel = 'Loading company v2 insights'
}) => (
  <div className="space-y-6 p-6" aria-label={ariaLabel} role="status">
    <div
      className={`animate-pulse rounded-2xl border p-6 shadow-sm ${
        isDarkMode
          ? 'border-strokedark bg-boxdark'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div
        className={`h-6 w-56 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
      />
      <div
        className={`mt-3 h-4 w-96 max-w-full rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
      />
    </div>
    <div
      className={`animate-pulse rounded-2xl border p-6 shadow-sm ${
        isDarkMode
          ? 'border-strokedark bg-boxdark'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div
        className={`h-5 w-32 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
      />
      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map(index => (
          <div
            key={index}
            className={`h-32 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}
          />
        ))}
      </div>
    </div>
  </div>
);

const CompanyInsights = () => {
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendHistory, setTrendHistory] = useState({});
  const [lockedTrendMonth, setLockedTrendMonth] = useState('');
  const [hoveredTrendMonth, setHoveredTrendMonth] = useState('');
  const trendCacheRef = useRef({});
  const trendRequestIdRef = useRef(0);

  const fetchCompanyInsights = useCallback(
    endYearMonth =>
      sendRequest(
        '/performance/company-performance',
        'POST',
        endYearMonth ? { end_year_month: endYearMonth } : null
      ),
    []
  );

  const {
    selectedEndYearMonth,
    availableEndYearMonths,
    loading,
    data,
    insightFormatVersion,
    screenState,
    screenStateMeta,
    handleEndYearMonthChange
  } = usePerformanceMonthFilter(fetchCompanyInsights);

  const overlayContent = getProductivityScreenOverlayContent(
    screenState,
    screenStateMeta
  );
  const isScreenBlocked = isProductivityScreenBlocked(screenState);

  useEffect(() => {
    if (!selectedEndYearMonth || !data) {
      return;
    }

    trendCacheRef.current[selectedEndYearMonth] = data;
  }, [data, selectedEndYearMonth]);

  useEffect(() => {
    if (availableEndYearMonths.length < 2) {
      setTrendHistory({});
      setTrendLoading(false);
      return;
    }

    const monthsToFetch = [...availableEndYearMonths].reverse();
    const cachedHistory = monthsToFetch.reduce((acc, month) => {
      const monthData = trendCacheRef.current[month];
      if (monthData) {
        acc[month] = monthData;
      }
      return acc;
    }, {});

    setTrendHistory(cachedHistory);

    const missingMonths = monthsToFetch.filter(
      month => !trendCacheRef.current[month]
    );

    if (!missingMonths.length) {
      return;
    }

    const requestId = trendRequestIdRef.current + 1;
    trendRequestIdRef.current = requestId;
    setTrendLoading(true);

    Promise.all(
      missingMonths.map(async month => {
        const response = normalizeInsightsResponse(
          await fetchCompanyInsights(month)
        );
        return [month, response?.data ?? null];
      })
    )
      .then(entries => {
        if (trendRequestIdRef.current !== requestId) {
          return;
        }

        entries.forEach(([month, monthData]) => {
          if (monthData) {
            trendCacheRef.current[month] = monthData;
          }
        });

        setTrendHistory(
          monthsToFetch.reduce((acc, month) => {
            if (trendCacheRef.current[month]) {
              acc[month] = trendCacheRef.current[month];
            }
            return acc;
          }, {})
        );
      })
      .catch(error => {
        if (trendRequestIdRef.current === requestId) {
          console.error('Error fetching company insight trend data:', error);
        }
      })
      .finally(() => {
        if (trendRequestIdRef.current === requestId) {
          setTrendLoading(false);
        }
      });
  }, [availableEndYearMonths, fetchCompanyInsights]);

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const trendSeriesData = useMemo(
    () =>
      [...availableEndYearMonths]
        .reverse()
        .map(month => {
          const monthData = trendHistory[month];

          if (!monthData) {
            return null;
          }

          const point = {
            month,
            label: formatEvaluationInterval(
              monthData.start_year_month,
              monthData.end_year_month,
              { separator: ' - ', fallback: formatEndYearMonth(month) }
            )
          };

          SCORE_SERIES.forEach(item => {
            point[item.key] =
              typeof monthData[item.key] === 'number'
                ? monthData[item.key]
                : null;
          });

          return point;
        })
        .filter(
          point =>
            point &&
            SCORE_SERIES.every(item => typeof point[item.key] === 'number')
        ),
    [availableEndYearMonths, trendHistory]
  );

  const hasTrendData = trendSeriesData.length >= 2;

  useEffect(() => {
    if (!hasTrendData) {
      setLockedTrendMonth('');
      setHoveredTrendMonth('');
      return;
    }

    const defaultMonth =
      selectedEndYearMonth ||
      trendSeriesData[trendSeriesData.length - 1]?.month ||
      '';
    setLockedTrendMonth(defaultMonth);
    setHoveredTrendMonth(defaultMonth);
  }, [hasTrendData, selectedEndYearMonth, trendSeriesData]);

  const activeTrendMonth = hoveredTrendMonth || lockedTrendMonth;
  const activeMonthData =
    (activeTrendMonth && trendHistory[activeTrendMonth]) || data;
  const activeMonthLabel = activeTrendMonth
    ? formatEvaluationInterval(
        activeMonthData?.start_year_month,
        activeMonthData?.end_year_month,
        { separator: ' - ', fallback: formatEndYearMonth(activeTrendMonth) }
      )
    : formatEvaluationInterval(data?.start_year_month, data?.end_year_month, {
        separator: ' - ',
        fallback: formatEndYearMonth(data?.end_year_month)
      });

  const trendChartOptions = useMemo(() => {
    const baseOptions = buildTrendChartOptions(trendSeriesData, isDarkMode);

    return {
      ...baseOptions,
      plotOptions: {
        ...baseOptions.plotOptions,
        series: {
          ...baseOptions.plotOptions.series,
          point: {
            events: {
              mouseOver() {
                setHoveredTrendMonth(this.options.custom?.sourceMonth || '');
              },
              click() {
                const nextMonth = this.options.custom?.sourceMonth || '';
                setLockedTrendMonth(nextMonth);
                setHoveredTrendMonth(nextMonth);
              }
            }
          }
        }
      },
      series: SCORE_SERIES.map(item => ({
        name: item.title,
        data: trendSeriesData.map(point => ({
          y: point[item.key],
          custom: {
            sourceMonth: point.month
          },
          marker:
            point.month === lockedTrendMonth
              ? {
                  enabled: true,
                  radius: 5,
                  fillColor: item.color,
                  lineColor: '#ffffff',
                  lineWidth: 2
                }
              : point.month === hoveredTrendMonth
                ? {
                    enabled: true,
                    radius: 5,
                    fillColor: isDarkMode ? '#24303f' : '#ffffff',
                    lineColor: item.color,
                    lineWidth: 2
                  }
                : undefined
        })),
        color: item.color
      }))
    };
  }, [activeTrendMonth, isDarkMode, trendSeriesData]);

  const isV2Insight =
    insightFormatVersion === 'v2' ||
    activeMonthData?.insight_format_version === 'v2';

  // Keep the first request visibly loading even before the API tells us which
  // insight format this workspace uses.
  if (loading && !isV2Insight) {
    return (
      <V2CompanyInsightsSkeleton
        isDarkMode={isDarkMode}
        ariaLabel="Loading company insights"
      />
    );
  }

  if (isV2Insight) {
    if (loading || !activeMonthData) {
      return <V2CompanyInsightsSkeleton isDarkMode={isDarkMode} />;
    }
    const observation = activeMonthData.observation || {};
    const strengthCount = activeMonthData.signals?.strengths?.length || 0;
    const watchoutCount = activeMonthData.signals?.watchouts?.length || 0;
    const signalTimeline = [...availableEndYearMonths]
      .reverse()
      .map(month => {
        const snapshot = trendHistory[month];
        if (snapshot?.insight_format_version !== 'v2') return null;
        return {
          month,
          strengths: (snapshot.signals?.strengths || []).slice(0, 1),
          watchouts: (snapshot.signals?.watchouts || []).slice(0, 1)
        };
      })
      .filter(Boolean);
    return (
      <div
        className={`relative space-y-6 p-4 sm:p-6 ${isDarkMode ? 'text-slate-100' : ''}`}
      >
        <div
          className={`relative overflow-hidden rounded-2xl border p-6 shadow-sm ${isDarkMode ? 'border-strokedark bg-boxdark' : 'border-slate-200 bg-white'}`}
        >
          <div className="absolute -right-14 -top-20 h-44 w-44 rounded-full bg-primary/10 blur-2xl" />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <InsightsOutlined />
              </div>
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                    Monthly intelligence
                  </span>
                  <span className="text-xs font-medium text-slate-400">
                    Updated for {activeMonthLabel}
                  </span>
                </div>
                <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Company engineering health
                </h3>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Evidence-based strengths and watchouts from the last 12
                  completed months.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:justify-end">
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 dark:border-strokedark dark:bg-slate-800 dark:text-slate-200">
                <CalendarMonth fontSize="small" className="text-primary" />
                <span>{activeMonthLabel}</span>
              </div>
              <PerformanceMonthPicker
                value={selectedEndYearMonth}
                options={availableEndYearMonths}
                onChange={handleEndYearMonthChange}
                disabled={loading}
              />
            </div>
          </div>
          <div className="relative mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-strokedark">
            <span className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-400">
              <CheckCircleOutline fontSize="small" /> {strengthCount} Strengths
            </span>
            <span className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-bold text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-400">
              <WarningAmberOutlined fontSize="small" /> {watchoutCount}{' '}
              Watchouts
            </span>
          </div>
          <div className="relative mt-6 flex items-center gap-2 border-t border-slate-100 pt-4 text-xs font-semibold dark:border-strokedark">
            {observation.status === 'sufficient' ? (
              <CheckCircleOutline
                className="text-emerald-500"
                fontSize="small"
              />
            ) : (
              <WarningAmberOutlined
                className="text-amber-500"
                fontSize="small"
              />
            )}
            <span className="text-slate-600 dark:text-slate-300">
              {observation.status === 'sufficient'
                ? 'Sufficient 12-month observation'
                : 'Limited observation'}
            </span>
            <span className="ml-1 text-slate-400">•</span>
            <span className="text-slate-400">
              Signals are based on completed monthly data
            </span>
          </div>
        </div>
        <div
          className={`rounded-2xl border p-5 shadow-sm sm:p-6 ${isDarkMode ? 'border-strokedark bg-boxdark' : 'border-slate-200 bg-white'}`}
        >
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h4 className="text-lg font-bold text-slate-900 dark:text-white">
                Key signals
              </h4>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                The themes shaping your engineering health this period.
              </p>
            </div>
            <span className="hidden rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500 sm:inline-flex dark:bg-slate-800 dark:text-slate-300">
              Click a signal to expand
            </span>
          </div>
          <SignalInsights signals={activeMonthData.signals} />
        </div>
        {signalTimeline.length >= 2 ? (
          <div
            className={`rounded-2xl border p-5 shadow-sm sm:p-6 ${isDarkMode ? 'border-strokedark bg-boxdark' : 'border-slate-200 bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <TrendingUp fontSize="small" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-slate-900 dark:text-white">
                  Signal history
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  How the conversation has evolved over time.
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {signalTimeline.map(point => (
                <div
                  key={point.month}
                  className="rounded-xl border border-slate-100 bg-slate-50/80 p-4 text-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:bg-white dark:border-strokedark dark:bg-slate-800/70 dark:hover:bg-boxdark"
                >
                  <p className="mb-3 font-bold text-slate-800 dark:text-slate-100">
                    {formatEndYearMonth(point.month)}
                  </p>
                  {point.strengths.map(signal => (
                    <p
                      key={signal.id}
                      className="mt-2 text-emerald-700 dark:text-emerald-400"
                    >
                      + {signal.title}
                    </p>
                  ))}
                  {point.watchouts.map(signal => (
                    <p
                      key={signal.id}
                      className="mt-1 text-amber-700 dark:text-amber-400"
                    >
                      ! {signal.title}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {!loading && overlayContent && (
          <NoDataOverlay
            title={overlayContent.title}
            body={overlayContent.body}
            ctaLabel={overlayContent.ctaLabel}
            ctaPath={overlayContent.ctaPath}
          />
        )}
      </div>
    );
  }

  if (loading && !insightFormatVersion) {
    return <Loading type="card" />;
  }

  return (
    <div
      className={`relative space-y-6 p-4 sm:p-6 ${isDarkMode ? 'text-slate-100' : ''}`}
    >
      <div
        className={`relative overflow-hidden rounded-2xl border p-5 shadow-sm sm:p-6 ${
          isDarkMode
            ? 'border-strokedark bg-boxdark'
            : 'border-slate-200 bg-gradient-to-br from-white via-white to-blue-50/70'
        }`}
      >
        <div className="absolute -right-16 -top-24 h-48 w-48 rounded-full bg-blue-500/10 blur-2xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-500">
              <InsightsOutlined />
            </div>
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                  Company intelligence
                </span>
                <span className="text-xs text-slate-400">Monthly view</span>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                Engineering productivity
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                Track performance, efficiency, and sustainability in one clear
                view.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:justify-end">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 dark:border-strokedark dark:bg-slate-800 dark:text-slate-200">
              <CalendarMonth fontSize="small" className="text-primary" />
              <span>{activeMonthLabel}</span>
            </div>
            <PerformanceMonthPicker
              value={selectedEndYearMonth}
              options={availableEndYearMonths}
              onChange={handleEndYearMonthChange}
              disabled={loading}
            />
          </div>
        </div>
      </div>
      {hasTrendData ? (
        <div
          className={`rounded-2xl border p-5 shadow-sm ${
            isDarkMode
              ? 'border-strokedark bg-boxdark'
              : 'border-slate-200 bg-white'
          }`}
          onMouseLeave={() => setHoveredTrendMonth(lockedTrendMonth)}
        >
          <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h4 className="flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                <TrendingUp fontSize="small" className="text-primary" />
                Productivity trend
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Hover a month to preview its insight cards. Click a month to
                keep it selected.
              </p>
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              {trendLoading ? (
                <span className="inline-flex w-28">
                  <Loading type="inline" />
                </span>
              ) : (
                `Active month: ${activeMonthLabel}`
              )}
            </div>
          </div>
          <HighchartsReact
            highcharts={Highcharts}
            options={trendChartOptions}
          />
        </div>
      ) : null}

      <div
        className={`grid grid-cols-1 gap-6 md:grid-cols-3 ${
          !loading && isScreenBlocked
            ? 'pointer-events-none select-none blur-sm'
            : ''
        }`}
      >
        <DashboardCard
          title="Performance"
          score={activeMonthData?.performance_score}
          reason={activeMonthData?.performance_reason}
          advices={activeMonthData?.performance_improvement_advices}
          loading={loading}
        />
        <DashboardCard
          title="Efficiency"
          score={activeMonthData?.efficiency_score}
          reason={activeMonthData?.efficiency_reason}
          advices={activeMonthData?.efficiency_improvement_advices}
          loading={loading}
        />
        <DashboardCard
          title="Sustainability"
          score={activeMonthData?.sustainability_score}
          reason={activeMonthData?.sustainability_reason}
          advices={activeMonthData?.sustainability_improvement_advices}
          loading={loading}
        />
      </div>

      {!loading && overlayContent && (
        <NoDataOverlay
          title={overlayContent.title}
          body={overlayContent.body}
          ctaLabel={overlayContent.ctaLabel}
          ctaPath={overlayContent.ctaPath}
        />
      )}
    </div>
  );
};

export default CompanyInsights;
